import { apiCall } from 'app/shared/lib/essette-api';
import { autoinject, bindingMode, computedFrom } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { UserService } from 'app/core/services/user-service';
import { UserReloadEvent } from 'app/core/lib/events';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { Toaster } from 'app/shared/lib/toaster';
import { UserAssociatedProviders } from 'app/core/components/users/user-associated-providers';
import * as Core from 'app/core/models/essette-api';

@autoinject()
export class EditUserPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.objectNull({ defaultBindingMode: bindingMode.toView })
    public user: Core.User | null = null;
    protected userId!: string;

    protected twoFactorAuthenticationIsEnabled: boolean = false;
    protected allowUserChoiceIsEnabled: boolean = false;
    protected preserveExistingUserContext: boolean = false;
    private subscriptions: Array<Subscription> = [];

    // is true when a user's details are synched with a Sign On Provider, false otherwise
    @computedFrom('user.isSignOnProvider', 'preserveExistingUserContext')
    protected get isUserSynched(): boolean {
        return !!this.user?.isSignOnProvider && this.preserveExistingUserContext == false;
    }

    ////****************** Element References ***************************************////
    protected $associatedProviders!: UserAssociatedProviders;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly userService: UserService,
        private readonly events: EventAggregator,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;

    }

    protected async activate({ userId }: { userId: string }): Promise<void | false> {
        this.userId = userId;
        await this.loadUser(userId);
        await this.loadUserSettings();
    }

    protected attached() {
        this.subscriptions.push(
            this.events.subscribe(UserReloadEvent, (e: UserReloadEvent) => this.reload())
        );
    }

    ////****************** Functions ************************************************////

    protected reload(): void {
        this.loadUser(this.userId);
        this.loadUserSettings();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadUser(userId: string): Promise<void> {
        const includes: string[] =
            [
                "login",
                "phoneNumber.primary",
                "phoneNumber.cell",
                "filters",
                "locations"
            ];
        const fields: string[] =
            [
                "description",
                "code",
                "login.loginName",
                "login.authenticationMethod.*",
                "login.signOnProvider.id",
                "name.first",
                "name.last",
                "emailAddress",
                "title",
                "department",
                "twoFactorAuthentication.optedIn",
                "phoneNumber.primary.number",
                "phoneNumber.primary.extension",
                "phoneNumber.cell.number",
                "phoneNumber.cell.extension",
                "filters.description",
                "locations.description",
                "isSignOnProvider",
                "isDirectoryService"
            ];
        try {
            this.state.beginLoad();

            const response = await this.userService.loadUser(userId, includes, fields);
            this.user = response.results[0];
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadUserSettings(): Promise<void> {
        const response = await this.userService.loadUserSettings();
        this.twoFactorAuthenticationIsEnabled = response.settings.twoFactorAuthentication.$isEnabled;
        this.allowUserChoiceIsEnabled = response.settings.twoFactorAuthentication.allowUserChoice.$isEnabled;
        this.preserveExistingUserContext = response.settings.sso.preserveExistingUserContexts.$isEnabled;
    }

    @apiCall
    protected async activateUser(): Promise<void> {
        if(!this.user?.id) return;
        const isActive = this.user.isActive || false;

        // Show confirmation dialog only if deactivating
        if(!isActive) {
            const result = await confirmation.showDeactivate('core:user.$', 1, false);
            if(!(result.dialogResponse === confirmation.options.OK)) {
                this.user.isActive = true;
                return;
            }
        }

        try {
            this.state.beginSave();
            const response = await this.userService.activateUser(this.user.id, isActive);
            this.toaster.standard.activate('core:user.$', isActive, response.isSuccessful);
            this.$associatedProviders.reload();
        } catch(error) {
            this.toaster.standard.activate('core:user.$', isActive, false);
            throw error;
        } finally {
            this.state.endSave();
        }
    }
}
